import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getCentrosCosto, createCentroCosto, updateCentroCosto, deleteCentroCosto } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, FolderTree } from 'lucide-react';

const TIPOS = ['TIENDA', 'AREA', 'PROYECTO', 'OTRO'];

const CentrosCostoPage = () => {
  const { empresaActual } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ nombre: '', tipo: 'OTRO', activo: true });

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await getCentrosCosto(empresaActual.id);
      setItems(res.data);
    } catch (error) {
      toast.error('Error al cargar centros de costo');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => { setFormData({ nombre: '', tipo: 'OTRO', activo: true }); setEditingItem(null); };

  const handleOpenDialog = (item = null) => {
    if (item) { setEditingItem(item); setFormData({ nombre: item.nombre, tipo: item.tipo, activo: item.activo }); }
    else { resetForm(); }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) { toast.error('El nombre es requerido'); return; }
    const payload = { ...formData, empresa_id: empresaActual.id };
    try {
      if (editingItem) { await updateCentroCosto(editingItem.id, payload); toast.success('Centro actualizado'); }
      else { await createCentroCosto(payload); toast.success('Centro creado'); }
      setDialogOpen(false); resetForm(); loadData();
    } catch (error) { toast.error('Error al guardar'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este centro de costo?')) return;
    try { await deleteCentroCosto(id); toast.success('Centro eliminado'); loadData(); }
    catch (error) { toast.error('Error al eliminar'); }
  };

  if (!empresaActual) return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FolderTree className="w-8 h-8 text-emerald-600" />
          <h1 className="text-2xl font-bold text-gray-900">Centros de Costo</h1>
        </div>
        <Button onClick={() => handleOpenDialog()}><Plus className="w-4 h-4 mr-2" />Nuevo Centro</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hay centros de costo</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.nombre}</TableCell>
                    <TableCell>{item.tipo}</TableCell>
                    <TableCell><Badge className={item.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>{item.activo ? 'Activo' : 'Inactivo'}</Badge></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingItem ? 'Editar' : 'Nuevo'} Centro de Costo</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Nombre *</Label><Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} required /></div>
            <div>
              <Label>Tipo</Label>
              <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CentrosCostoPage;
